import React, { useState, useEffect } from "react";
import { Contract, ethers } from "ethers";
import {
  Container,
} from "react-bootstrap";
import {
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { useTranslation } from "react-i18next";
import { isTestEnv, presaleAbi, presaleAddress } from "../utils/contants";
import { toast } from "react-toastify";
import { Affiliate } from "../types/Affiliate.ts";
import { getAffiliateMap } from "../store/firestore.ts";
import { AffiliateData } from "../types/AffiliateData.ts";
import AffilaiteTable from "../components/AffiliateTable.tsx";

const RevokedAffiliates: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { address } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();

  // Contract and affiliate state
  const [presaleContract, setPresaleContract] =
    useState<ethers.Contract | null>(null);
  const [affilaiteDataMap, setAffiliateMap] = useState<
    Map<string, AffiliateData>
  >(new Map());
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [totalAffiliates, setTotalAffiliates] = useState(0);
  const [contractOwner, setContractOwner] = useState("");

  // Pagination & search
  const pageSize = 10;
  const [offset, setOffset] = useState(0);
  

  const init = async () => {
    try {
      console.log("init called");
      if (!walletProvider) return;
      console.log("init worked");
      const provider = new ethers.BrowserProvider(walletProvider);
      const signer = await provider.getSigner();
      const contract = new Contract(presaleAddress, presaleAbi, signer);
      const ownerAddress = await contract.owner();
      setContractOwner(ownerAddress);
      setPresaleContract(contract);
    } catch (error) {
      console.error(error);
      toast.info(t("info.error"));
    }
  };

  const parseAffiliates = (data: any[]) => {
    let affs: Affiliate[] = [];
    for (let i = 0; i < data.length; i++) {
      let affiliate = Affiliate.fromEthersResponse(data[i]);
      // Stop if we hit a zero address
      if (affiliate.user === ethers.ZeroAddress) break;
      affs.push(affiliate);
    }
    return affs;
  };

  const fetchAffiliates = async () => {
    if (!presaleContract) return;
    try {
      // Call getAffiliates with pageSize and offset
      const result = await presaleContract.getAffiliates(pageSize, offset);
      const affs = parseAffiliates(result);
      setAffiliates(affs);
      // Fetch total affiliates from contract (if available)
      if (presaleContract.getTotalAffiliates) {
        const total = await presaleContract.getTotalAffiliates();
        setTotalAffiliates(Number(total));
      }
    } catch (error) {
      console.error("Error fetching affiliates:", error);
    }
  };

  // Pagination handlers
  const handlePrevPage = async () => {
    if (offset > 0) {
      setOffset(offset - 1);
    }
  };

  const handleNextPage = async () => {
    // Ensure that there is another page (using totalAffiliates if available)
    if (totalAffiliates > (offset + 1) * pageSize) {
      setOffset(offset + 1);
    }
  };

  // Whenever offset changes, re-fetch affiliates
  useEffect(() => {
    if (presaleContract) {
      fetchAffiliates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  useEffect(() => {
    init();
  }, [walletProvider]);

  useEffect(() => {
    fetchAffiliates();
  }, [presaleContract]);

  const initDB = async () => {
    let affiliates = await getAffiliateMap();
    setAffiliateMap(affiliates);
  };

  useEffect(() => {
    initDB();
  }, []);

  // Only display details if connected address equals contract owner
  if (!isTestEnv && (!address || address.toLowerCase() !== contractOwner.toLowerCase())) {
    return (
      <Container className="text-center mt-5">
        <h3>{t('info.only_owner')}</h3>
      </Container>
    );
  }

 
  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">{t("affiliates.revoked_affiliates")}</h2>
      <AffilaiteTable revokedOnly={true} />
    </Container>
  );
};

export default RevokedAffiliates;
